import React, {useState} from 'react'
import moment from 'moment'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {BadgeStatePolicy} from './BadgeStatePolicy'
import {ModalPolicyFamily} from './ModalPolicyFamily'
import { Badge } from 'react-bootstrap'
import {STATUS_POLICY,   getDocumentVariant, getPaymentVariant} from '../../../helpers/consts'
import {useGetUser} from '../../../store/userStore'

/**
 * Llaves NUEVAS que solo se consumen vía el helper `t()`: se declaran aquí para que el extractor
 * las capture (guía §17.12).
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const M = defineMessages({
  titlePolicy: {id: 'policies.detail.summary.titlePolicy', defaultMessage: 'Policy Summary'},
  titleQuote: {id: 'policies.detail.summary.titleQuote', defaultMessage: 'Quote Summary'},
  canceled: {id: 'policies.detail.summary.canceled', defaultMessage: 'Canceled'},
  completed: {id: 'policies.detail.summary.completed', defaultMessage: 'Completed'},
})

interface PolicySummaryProps {
  updateStatuses: () => void
  assignAgent: () => void
  changeSource: () => void
  setTypeAssign: React.Dispatch<React.SetStateAction<'ASSIGN_TO' | 'USER_TO'>>
  data: any
  policy: string
  isQuote?: boolean
  category?: string | null
}

export const PolicySummary: React.FC<PolicySummaryProps> = ({
  updateStatuses,
  assignAgent,
  changeSource,
  setTypeAssign,
  data,
  policy,
  isQuote,
  category
}) => {
  const intl = useIntl()
  const t = (id: string, defaultMessage: string) => intl.formatMessage({id, defaultMessage})
  const hash = '#'
  const dotSep = ' · '
  const colonSep = ': '
  const sp = ' '
  const nr = t('policies.list.preview.value.nr', 'N/R')
  const na = t('common.value.notAvailable', 'N/A')
  const [showFamilyModal, setShowFamilyModal] = useState(false)
  const primaryGroup = useGetUser((s) => s.user?.user?.primaryGroup)
  const isAgent = primaryGroup?.name === 'Agente'
  const isAsesor = primaryGroup?.name === 'Asesor'
  const canViewSource = !isAgent  // Everyone except Agentes can view
  const canEditSource = !isAgent && !isAsesor  // Only Supervisores/Admins can edit
  const showDocumentsDueDate = (data.stateDocs === 'PENDING' || data.stateDocs === 'PROCESSING') && !!data.documentsDueDate
  const daysUntilDocumentsDueDate = showDocumentsDueDate
    ? moment(data.documentsDueDate).startOf('day').diff(moment().startOf('day'), 'days')
    : null
  const documentsDueDateIsOverdue = daysUntilDocumentsDueDate !== null && daysUntilDocumentsDueDate < 0
  const dueDateSuffix =
    daysUntilDocumentsDueDate === null
      ? ''
      : sp +
        (daysUntilDocumentsDueDate >= 0
          ? intl.formatMessage(
              {
                id: 'policies.detail.summary.daysLeft',
                defaultMessage: '({days, plural, one {# day left} other {# days left}})',
              },
              {days: daysUntilDocumentsDueDate}
            )
          : intl.formatMessage(
              {
                id: 'policies.detail.summary.daysOverdue',
                defaultMessage: '({days, plural, one {# day overdue} other {# days overdue}})',
              },
              {days: Math.abs(daysUntilDocumentsDueDate)}
            ))

  return (
  <div>
    <div className='kt-heading kt-heading--sm custom-border-brand my-3'>
      {isQuote
        ? t('policies.detail.summary.titleQuote', 'Quote Summary')
        : t('policies.detail.summary.titlePolicy', 'Policy Summary')}
    </div>
    <div className='kt-widget__info'>
      <div className='policy-status-edit-modal'>
        <span className='kt-widget__label '>
          <FormattedMessage
            id='policies.detail.summary.agentOwner'
            defaultMessage='Agent (Owner):'
          />
        </span>{sp}
        <i
          onClick={() => {
            setTypeAssign('USER_TO')
            assignAgent()
          }}
          className='bi bi-person-bounding-box set-important'
        ></i>
      </div>
      <span className='kt-widget__data'>
        {data.user?.firstName} {data.user?.lastName}
      </span>
    </div>
    <div className='kt-widget__info'>
      <div className='policy-status-edit-modal'>
        <span className='kt-widget__label '>
          <FormattedMessage id='policies.detail.summary.assignTo' defaultMessage='Assign To:' />
        </span>{sp}
        <i
          onClick={() => {
            setTypeAssign('ASSIGN_TO')
            assignAgent()
          }}
          className='bi bi-person-bounding-box set-important'
        ></i>
      </div>
      <span className='kt-widget__data'>
        {data.assignedTo?.firstName} {data.assignedTo?.lastName}
      </span>
    </div>
    <div className='kt-widget__info'>
      <div className='policy-status-edit-modal'>
        <span className='kt-widget__label '>
          <FormattedMessage id='policies.detail.summary.createdBy' defaultMessage='Created by:' />
        </span>{sp}
      </div>
      <span className='kt-widget__data'>
        {data.createdBy?.firstName} {data.createdBy?.lastName}
      </span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>
        <FormattedMessage id='policies.detail.summary.internalCode' defaultMessage='Internal code:' />
      </span>
      <span className='kt-widget__data'>{policy}</span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>
        <FormattedMessage id='policies.list.page.cell.carrier' defaultMessage='Carrier:' />
      </span>
      <span className='kt-widget__data'>{data?.carrier?.name || nr}</span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>
        <FormattedMessage id='policies.detail.summary.effective' defaultMessage='Effective date:' />
      </span>
      <span className='kt-widget__data' id='effective_label'>
        {data.effectiveDate ? moment(data.effectiveDate).format('DD MMM, YY') : nr}
      </span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>
        <FormattedMessage
          id='policies.detail.summary.applicants'
          defaultMessage='Number of applicants:'
        />
      </span>
      <span className='kt-widget__data' id='no_applicants'>
        {data.applicants ? Number(data.applicants) : nr}
      </span>
    </div>
    <div className='kt-widget__info'>
      <div className='policy-status-edit-modal'>
        <span className='kt-widget__label '>
          <FormattedMessage id='policies.detail.summary.status' defaultMessage='Status:' />
        </span>{sp}
        {!isQuote && <i onClick={updateStatuses} className='bi bi-pencil-square set-important'></i>}
      </div>
      <span className='kt-widget__data'>
        <BadgeStatePolicy status={data.status} color={data.color} />
      </span>
    </div>
    {category !== STATUS_POLICY.PENDING && (
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          {(category === STATUS_POLICY.CANCELED
            ? t('policies.detail.summary.canceled', 'Canceled')
            : category === STATUS_POLICY.COMPLETED
              ? t('policies.detail.summary.completed', 'Completed')
              : category) + colonSep}
        </span>
        <span className='kt-widget__data'>
          {isQuote
            ? moment(data.created).format('DD MMM YYYY')
            : moment(data.canceledDate || data.completedDate).format('DD MMM YYYY')}
        </span>
      </div>
    )}
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>
        <FormattedMessage
          id='policies.detail.summary.documentsStatus'
          defaultMessage='Documents status:'
        />
      </span>
      <span className='kt-widget__data'>
        <Badge className="badge-sm" bg={getDocumentVariant(data.stateDocs)}>
          {data.stateDocs || na}
        </Badge>
      </span>
    </div>
    {showDocumentsDueDate && (
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage
            id='policies.detail.summary.docsDueDate'
            defaultMessage='Documents due date:'
          />
        </span>
        <span className='kt-widget__data'>
          <Badge
            className="badge-sm"
            bg={documentsDueDateIsOverdue ? 'danger' : 'warning'}
            text={documentsDueDateIsOverdue ? 'white' : 'dark'}
          >
            {moment(data.documentsDueDate).format('DD MMM, YY')}
            {daysUntilDocumentsDueDate !== null && dueDateSuffix}
          </Badge>
        </span>
      </div>
    )}
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>
        <FormattedMessage
          id='policies.detail.summary.paymentStatus'
          defaultMessage='Payment status:'
        />
      </span>
      <span className='kt-widget__data'>
        <Badge
          className="badge-sm"
          bg={getPaymentVariant(data.paymentStatus)}
          text={getPaymentVariant(data.paymentStatus) === 'danger' ? 'white' : undefined}
        >
          {data.paymentStatus || na}
        </Badge>
      </span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>
        <FormattedMessage id='policies.detail.summary.lastUpdate' defaultMessage='Last update:' />
      </span>
      <span className='kt-widget__data' id='last_update_label'>
        {data.updated ? moment(data.updated).format('DD MMM, YY') : nr}
      </span>
    </div>
    {canViewSource && (
      <div className='kt-widget__info'>
        <div className='policy-status-edit-modal'>
          <span className='kt-widget__label '>
            <FormattedMessage id='policies.list.idCard.sourceLabel' defaultMessage='Source:' />
          </span>{sp}
          {canEditSource && (
            <i onClick={changeSource} className='bi bi-pencil-square set-important'></i>
          )}
        </div>
        <span className='kt-widget__data'>
          {data.leadSource?.name || nr}
        </span>
      </div>
    )}
    {(data.policyFamily?.length > 1 || data.contactNetwork?.length > 0) && (
      <>
        <div className='kt-widget__info'>
          {data.sourcePolicy ? (
            <>
              <span className='kt-widget__label'>
                <FormattedMessage
                  id='policies.detail.summary.duplicatedFrom'
                  defaultMessage='Duplicated from:'
                />
              </span>
              <span
                className='kt-widget__data text-primary'
                style={{cursor: 'pointer', textDecoration: 'underline'}}
                onClick={() => setShowFamilyModal(true)}
              >
                {hash}{data.sourcePolicy.pk}
                {data.sourcePolicy.effectiveDate
                  ? dotSep + moment(data.sourcePolicy.effectiveDate).format('DD MMM, YY')
                  : ''}
                <i className='bi bi-diagram-3 ms-1' style={{fontSize: '0.8rem'}} />
              </span>
            </>
          ) : (
            <>
              <span className='kt-widget__label'>
                <FormattedMessage
                  id='policies.detail.summary.relatedPolicies'
                  defaultMessage='Related policies:'
                />
              </span>
              <span
                className='kt-widget__data text-primary'
                style={{cursor: 'pointer', textDecoration: 'underline'}}
                onClick={() => setShowFamilyModal(true)}
              >
                <FormattedMessage
                  id='policies.detail.summary.policiesCount'
                  defaultMessage='{count, plural, one {# policy} other {# policies}}'
                  values={{
                    count: (data.policyFamily?.length ?? 0) + (data.contactNetwork?.length ?? 0),
                  }}
                />
                <i className='bi bi-diagram-3 ms-1' style={{fontSize: '0.8rem'}} />
              </span>
            </>
          )}
        </div>
        <ModalPolicyFamily
          isOpen={showFamilyModal}
          onClose={() => setShowFamilyModal(false)}
          items={data.policyFamily ?? []}
          household={data.contactNetwork ?? []}
          currentPk={Number(policy)}
        />
      </>
    )}
  </div>
  )
}
